import { Component, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { HrService } from '../../hr.service';
import { AlertService } from '@app/shared/alert.service';
import { SignaturePadComponent } from '@app/shared/signature-pad/signature-pad.component';
import { HrDocument } from '@app/shared/_models/hr';
import { EVENTTYP_HR_TIMESHEET_COUNTERSIGN, OBJECTTYP_HR_TIMESHEET } from '@app/shared/constants';
import { environment } from '../../../../environments/environment';
import { hoursLabel } from '../../hr-schedule-engine';
import { weekSignLabel } from '../../hr-timesheet-sign';

// Un relevé signé par son salarié, en attente du visa — la ligne du dialog.
export interface CountersignRow {
	doc: HrDocument;
	name: string;							// le salarié
	week: string;							// lundi « YYYY-MM-DD »
	total_min: number;
	signed_at: string;				// RFC3339 de la signature salarié
}

export interface CountersignDialogData {
	rows: CountersignRow[];
}

// Le VISA du responsable sur les relevés d'heures signés par les salariés. Il voit le PDF tel
// que le salarié l'a signé, trace son visa : le serveur revérifie que le décompte n'a pas bougé
// depuis (event 4402) et regénère le PDF avec les deux signatures.
@Component({
	selector: 'app-hr-countersign-dialog',
	standalone: true,
	imports: [MatDialogModule, MatButtonModule, MatIconModule, MatTooltipModule, SignaturePadComponent],
	templateUrl: './countersign-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './countersign-dialog.component.scss',
})
export class CountersignDialogComponent {
	@ViewChild(SignaturePadComponent) pad?: SignaturePadComponent;

	private hrService = inject(HrService);
	private alertService = inject(AlertService);
	public dialogRef = inject<MatDialogRef<CountersignDialogComponent>>(MatDialogRef);
	public data: CountersignDialogData = inject(MAT_DIALOG_DATA);

	public selected: CountersignRow | null = null;
	public posting = false;
	// Au moins un visa posé : l'écran appelant recharge à la fermeture.
	public countersignedSomething = false;

	h(min: number): string {
		return hoursLabel(min);
	}

	weekLabel(row: CountersignRow): string {
		return weekSignLabel(row.week);
	}

	signedLabel(row: CountersignRow): string {
		const d = row.signed_at;
		return d ? 'signé le ' + d.slice(8, 10) + '/' + d.slice(5, 7) + '/' + d.slice(0, 4) : '';
	}

	onSelect(row: CountersignRow): void {
		if (this.posting) return;
		this.selected = row;
		this.pad?.clear();
	}

	// Le PDF TEL QUE LE SALARIÉ L'A SIGNÉ — c'est lui qu'on vise, il s'ouvre d'un clic.
	onOpenDoc(row: CountersignRow): void {
		window.open(environment.server_url + '/attach/content/' + row.doc.id, '_blank');
	}

	signReady(): boolean {
		return !!this.selected && !this.posting && !!this.pad?.signatureValid();
	}

	onClear(): void {
		this.pad?.clear();
	}

	onCountersign(): void {
		if (!this.signReady() || !this.selected) return;
		this.posting = true;
		const row = this.selected;
		const payload = this.hrService.buildTimesheetCountersignPayload({
			id: row.doc.id,
			signature: this.pad!.toDataURL(),
		});
		this.hrService.postHrEvent(EVENTTYP_HR_TIMESHEET_COUNTERSIGN, OBJECTTYP_HR_TIMESHEET, payload).subscribe({
			next: () => {
				this.posting = false;
				this.countersignedSomething = true;
				this.data.rows = this.data.rows.filter(r => r !== row);
				this.selected = null;
				this.alertService.success('Relevé visé — le PDF porte les deux signatures');
			},
			// Refus métier (décompte changé depuis la signature, déjà visé…) : le message serveur
			// dit exactement quoi faire, on le montre tel quel.
			error: (e) => {
				this.posting = false;
				this.alertService.error('' + (e?.error ?? e?.message ?? 'Contre-signature refusée'));
			},
		});
	}

	onClose(): void {
		this.dialogRef.close(this.countersignedSomething);
	}
}
